import Constants from '@/Constants';
import { useAutomaticEnrollRule } from '@/hooks/useAutomaticEnrollRule';
import { useGroup } from '@/hooks/useGroup';
import { AutomaticEnrollRule } from '@/models/AutomaticEnroll';
import { Group } from '@/models/Group';
import { GroupCard } from '@/pages/People/PersonGroup/GroupCard';
import http from '@/services/HttpService';
import { ApplicationState } from '@/store';
import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/esm/Modal';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Loader from '../Loader';
import './styles.scss';

interface GroupActionModalProps {
    modalVisible: boolean;
    onHide: CallableFunction;
    programClassId: number;
}

const GroupActionModal: React.FC<GroupActionModalProps> = ({ modalVisible, onHide, programClassId }: GroupActionModalProps) => {
    const { strings } = useSelector((state: ApplicationState) => ({ strings: state.locale.data }));
    const applicationId: number = useSelector((state: ApplicationState) => state.appOrganization.data.application.id);
    const organizationId: number = useSelector((state: ApplicationState) => state.appOrganization.data.organization.id);
    const [group, setGroup] = useState<Group[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    let callOnce: number = 1;

    const { response: groups } = useGroup(applicationId, organizationId);

    const { response: automaticEnrollRules, revalidateResult } = useAutomaticEnrollRule(programClassId);

    const getGroup = (groupId: number): Group => {
        return groups?.find(({ id }: Group) => id === groupId) as Group;
    };

    const getAutomaticEnrollRules = () => {
        return automaticEnrollRules && automaticEnrollRules.map((autEnrollRule: AutomaticEnrollRule) => getGroup(autEnrollRule.groupId));
    };

    const addAutomaticEnrollRule = (group: Group) => {
        http.post(Constants.LMS_AUTOMATIC_ENROLLMENT_URI, {
            active: false,
            groupId: group.id,
            programClassId,
            unenroll: false,
            onlyNotEnrolledStudents: true,
        }).then(() => {
            toast.success(strings.PUBLIC_ADDED_TO_CLASS);
            revalidateResult();
        });
    };

    const editAutomaticEnrollRule = (id: number, group: Group, programClassId: number) => {
        http.put(`${Constants.LMS_AUTOMATIC_ENROLLMENT_URI}/${id}`, {
            active: true,
            group,
            groupId: group.id,
            id,
            programClassId,
            unenroll: false,
            onlyNotEnrolledStudents: true,
        }).then(() => {
            if (callOnce === 1) {
                toast.success(strings.PUBLIC_ACTIVATED_IN_CLASS);
                callOnce++;
            }
        });
    };

    const removerAutomaticEnrollRule = (id: number) => {
        if (id) {
            const enrollIdRule = automaticEnrollRules?.find((autEnrollRule) => autEnrollRule.groupId === id) as AutomaticEnrollRule;
            removeAutomaticEnrollRule(enrollIdRule.id);
        }
    };

    const removeAutomaticEnrollRule = (id: number) => {
        http.delete(`${Constants.LMS_AUTOMATIC_ENROLLMENT_URI}/${id}`).then(() => {
            toast.success(strings.PUBLIC_REMOVE_CLASS);
        });
    };

    const addGroup = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const group: Group = getGroup(Number(e.target.value));
        setGroup((prevGroup) => {
            addAutomaticEnrollRule(group);
            return [
                ...prevGroup,
                {
                    id: group.id,
                    name: group.name,
                    organizationId: group.organizationId,
                    applicationId: group.applicationId,
                },
            ];
        });
    };

    const closeModal = () => {
        setGroup([]);
        onHide();
        revalidateResult();
    };

    const removeGroup = (id: number) => {
        const groupToRemove: Group = getGroup(id);
        removerAutomaticEnrollRule(groupToRemove.id);
        setGroup((prevGroup) => {
            const newArray = prevGroup.filter((group) => group.id !== groupToRemove.id);
            return [...newArray];
        });
    };

    const saveAutomaticEnrollRules = () => {
        automaticEnrollRules
            ?.filter((aut) => aut.active === false)
            .map((automaticEnrollRule: AutomaticEnrollRule) => {
                editAutomaticEnrollRule(automaticEnrollRule.id, getGroup(automaticEnrollRule.groupId), programClassId);
            });
        closeModal();
    };

    useEffect(() => {
        if (modalVisible) {
            revalidateResult();
            setLoading(true);
            if (automaticEnrollRules && automaticEnrollRules.length > 0) {
                setGroup(getAutomaticEnrollRules() as Group[]);
            }
            setLoading(false);
        }
    }, [modalVisible]);

    return (
        <Modal
            animation={true}
            backdropClassName="groupActionModal"
            centered
            keyboard={false}
            onHide={closeModal}
            scrollable={true}
            show={modalVisible}
            size="lg"
        >
            <div className="groupActionModal__sub-content">
                {loading ? (
                    <Loader />
                ) : (
                    <>
                        <Modal.Header closeButton>
                            <Modal.Title>
                                <h4 className="automatic-enroll">{strings.AUTOMATIC_ENROLL}</h4>
                                <p className="groupActionModal__title">{strings.PUBLIC_TARGET}</p>
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <select
                                className="groupActionModal__selected-option"
                                name="group"
                                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => addGroup(e)}
                            >
                                <option className="title">{strings.SELECTED_PUBLIC_TARGET}</option>
                                {groups &&
                                    groups.length > 0 &&
                                    groups.map(({ id, name }: Group) => {
                                        if (name !== 'All Users') {
                                            return (
                                                <option key={id} value={id}>
                                                    {name}
                                                </option>
                                            );
                                        }
                                    })}
                            </select>
                            <div>
                                <GroupCard data={group} remove={(id: number) => removeGroup(id)} showClassScreen={true} />
                            </div>
                        </Modal.Body>
                        <Modal.Footer>
                            <button className="btn btn-primary b-left" onClick={closeModal}>
                                {strings.CANCEL}
                            </button>
                            <button className="btn btn-primary b-rigth" disabled={group.length === 0} onClick={saveAutomaticEnrollRules}>
                                {strings.SAVE}
                            </button>
                        </Modal.Footer>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default GroupActionModal;
